/**
 * Lightweight content policy for user-chosen handles — applied to both
 * nicknames and to custom interest tags. Deliberately narrow: reject
 * obvious slurs, sexual/graphic terms, impersonation of platform roles,
 * and digit-heavy strings that look like phone numbers or URLs. Anything
 * subtler (creative misspellings, evasive leet-speak) is caught by the
 * moderation cascade once the user starts talking, and by reactive
 * moderator review.
 *
 * Matching strategy: lowercase the input, then substring-check against a
 * small fixed list. This catches casing variations and embedded forms
 * ("BigFaggotFan") without the false-positive problems of trying to
 * make a single regex tolerate inserted separators. Determined users
 * will always find work-arounds — that's what reactive moderation is
 * for. The aim here is to stop someone from picking a slur as their
 * literal display name, not to make a slur generator impossible.
 */

/**
 * Substrings that disqualify a nickname or tag if they appear in the
 * lowercased input. Kept narrow and obvious.
 */
const BLOCKLIST: readonly string[] = [
  // Racial / ethnic / national slurs
  'nigger',
  'nigga',
  'chink',
  'spic',
  'kike',
  'gook',
  'wetback',
  // Homophobic / transphobic slurs
  'faggot',
  'tranny',
  'dyke',
  // Misogynist slurs / graphic sexual
  'cunt',
  'whore',
  'slut',
  'rapist',
  'paedo',
  'pedo',
  // Strong profanity that has no place in a handle
  'fuck',
  'shit',
  'cock',
  'dickhead',
  // Graphic / hate
  'porn',
  'nazi',
  'hitler',
  'kkk',
  // Platform / authority impersonation
  'admin',
  'moderator',
  'lessalone',
  'staff',
  'support',
  'official',
];

/** Phone-number-shaped strings (3-3-4 or 10+ consecutive digits). */
const PHONE_PATTERN = /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b|\d{10,}/;
/** URLs and email addresses. */
const URL_PATTERN = /(?:https?:\/\/|www\.|@\w+\.\w+|\.com|\.net|\.org|\.io)/i;

export type NicknamePolicyResult = { ok: true } | { ok: false; reason: string };

export interface NicknamePolicyOptions {
  /** Min length, inclusive. Defaults to 2. */
  minLength?: number;
  /** Max length, inclusive. Defaults to 24. */
  maxLength?: number;
  /** Label used in error messages ("nickname" / "tag"). */
  label?: string;
}

/**
 * Runs a candidate nickname or tag against the policy. Returns ok=true if
 * it passes, or ok=false with a user-facing reason string suitable for
 * surfacing in the UI.
 */
export function checkNicknamePolicy(
  raw: string,
  options: NicknamePolicyOptions = {},
): NicknamePolicyResult {
  const minLength = options.minLength ?? 2;
  const maxLength = options.maxLength ?? 24;
  const label = options.label ?? 'nickname';
  const trimmed = raw.trim();

  if (trimmed.length < minLength) {
    return { ok: false, reason: `That ${label} is too short.` };
  }
  if (trimmed.length > maxLength) {
    return { ok: false, reason: `That ${label} is too long (max ${maxLength}).` };
  }

  if (PHONE_PATTERN.test(trimmed)) {
    return { ok: false, reason: `Please don’t put a phone number in your ${label}.` };
  }
  if (URL_PATTERN.test(trimmed)) {
    return { ok: false, reason: `Please don’t put a URL or email in your ${label}.` };
  }

  const lowered = trimmed.toLowerCase();
  for (const term of BLOCKLIST) {
    if (lowered.includes(term)) {
      return { ok: false, reason: `That ${label} isn’t allowed — try another.` };
    }
  }

  return { ok: true };
}
